import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import qs from "qs";
import appConfig from "../config";
import { toast } from "#/infra/Toastify";
import { isGatewayIdp } from "#/modules/auth/idp";

const onRequest = (config: AxiosRequestConfig): AxiosRequestConfig => config;
const onRequestError = (error: AxiosError): Promise<AxiosError> => Promise.reject(error);

const onResponse = (response: AxiosResponse): AxiosResponse => response;

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
  const statusCode = error.response?.status;

  if (statusCode === 401) {
    const requestUrl = error.config?.url ?? "";
    // sandbox scan 使用獨立的 service token，401 不代表 session 過期。
    // Cognito 的 GET /me bootstrap 由 OrgContext fail-open 處理，401 不可把使用者踢去 /login
    // （否則 AWS /me 一旦拒絕就會弄壞主線）；Entra /me 401 = token 過期，仍要轉登入。
    const skipRedirect = requestUrl.includes("/xecguard/v1/scan")
      || (!isGatewayIdp && requestUrl.endsWith("/internal/v1/me"));
    if (!skipRedirect) {
      // 清除快取的 token，避免 redirect 後仍讀到過期 token。
      //
      // 兩組前綴：`oidc.` 是 oidc-client-ts（Cognito / Entra），
      // `xecguard:google-` 是 Google BFF 流程自己存的。只清前者的話，
      // Google 這邊會 401 → 導去 /login → LoginPage 讀到仍在 sessionStorage
      // 的過期 token → 判定已登入 → 導回 /dashboard → 再 401，變成無限重整。
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith("oidc.") || key.startsWith("xecguard:google-"))
        .forEach((key) => sessionStorage.removeItem(key));
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }

  // /me bootstrap (+ /me/preferences) errors are owned by OrgContext / setDefaultOrg, which
  // give their own UX (gate / picker / toast). Swallow the generic toast here.
  if ((error.config?.url ?? "").includes("/internal/v1/me")) {
    return Promise.reject(error);
  }

  const data = error.response?.data as Record<string, unknown>;
  let message = String(
    (data?.error as Record<string, unknown>)?.message ?? data?.msg ?? error.message ?? "Request failed",
  );
  try {
    const parsed = JSON.parse(message);
    message = parsed?.error?.message ?? parsed?.message ?? message;
  } catch { /* not JSON, use as-is */ }
  toast.error(statusCode ? `[${statusCode}] ${message}` : message);
  return Promise.reject(error);
};

function setupInterceptorsTo(axiosInstance: AxiosInstance): AxiosInstance {
  // @ts-ignore
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(onResponse, onResponseError);
  return axiosInstance;
}

export const request = (config: { index?: number, total?: number } = {}): AxiosInstance => {
  const { index, total, ...restConfig } = config;
  const r = setupInterceptorsTo(
    axios.create({
      baseURL: appConfig.apiBaseUrl,
      withCredentials: false, // 不要設成true, 不然會有跨域問題
      timeout: 30000,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
      ...restConfig,
    }),
  );
  return r;
};

export default request;
